import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  FileJson, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Download,
  Copy,
  Eye,
  Search,
  RefreshCw,
  FileText,
  Pill,
  TestTube,
  Stethoscope,
  Syringe,
  FileUp,
  Heart,
  Receipt,
  ClipboardList,
  Loader2,
  ChevronRight,
  ChevronDown,
  User,
  Building2,
  Calendar,
  Shield,
  Info
} from 'lucide-react';
import api from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Document type configuration
const DOCUMENT_TYPES = [
  { value: 'prescription', label: 'Prescription', icon: Pill, color: 'text-blue-600', collection: 'prescriptions' },
  { value: 'discharge_summary', label: 'Discharge Summary', icon: FileText, color: 'text-purple-600', collection: 'discharge_summaries' },
  { value: 'lab_report', label: 'Lab Report', icon: TestTube, color: 'text-green-600', collection: 'lab_orders' },
  { value: 'op_consultation', label: 'OP Consultation', icon: Stethoscope, color: 'text-orange-600', collection: 'medical_records' },
  { value: 'immunization_record', label: 'Immunization Record', icon: Syringe, color: 'text-red-600', collection: 'immunizations' },
  { value: 'health_document', label: 'Health Document', icon: FileUp, color: 'text-indigo-600', collection: 'documents' },
  { value: 'wellness_record', label: 'Wellness Record', icon: Heart, color: 'text-pink-600', collection: 'wellness_records' },
  { value: 'invoice', label: 'Invoice/Billing', icon: Receipt, color: 'text-amber-600', collection: 'billing' }
];

// FHIR Resource icons
const RESOURCE_ICONS = {
  'Patient': User,
  'Practitioner': Stethoscope,
  'Organization': Building2,
  'Composition': FileText,
  'MedicationRequest': Pill,
  'Condition': AlertTriangle,
  'Encounter': Calendar,
  'DiagnosticReport': TestTube,
  'Observation': Eye,
  'Immunization': Syringe,
  'DocumentReference': FileUp,
  'CarePlan': ClipboardList,
  'ClinicalImpression': Stethoscope,
  'Invoice': Receipt,
  'ChargeItem': Receipt,
  'PaymentReconciliation': Receipt
};

export default function FHIRViewer() {
  const [documentType, setDocumentType] = useState('prescription');
  const [recordId, setRecordId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  
  // FHIR Bundle state
  const [fhirBundle, setFhirBundle] = useState(null);
  const [validation, setValidation] = useState(null);
  const [activeView, setActiveView] = useState('visual'); // visual, json, validation
  
  // Expanded resources in visual view
  const [expandedResources, setExpandedResources] = useState({});

  useEffect(() => {
    loadRecords();
  }, [documentType]);

  const loadRecords = async () => {
    setLoadingRecords(true);
    try {
      const docConfig = DOCUMENT_TYPES.find(d => d.value === documentType);
      let endpoint = '';
      
      switch (documentType) {
        case 'prescription':
          endpoint = '/api/prescriptions?limit=20';
          break;
        case 'discharge_summary':
          endpoint = '/api/discharge-summaries?limit=20';
          break;
        case 'lab_report':
          endpoint = '/api/lab-orders/orders?limit=20';
          break;
        case 'op_consultation':
          endpoint = '/api/medical-records?limit=20';
          break;
        case 'invoice':
          endpoint = '/api/billing?limit=20';
          break;
        default:
          // For immunization, health_document, wellness - may not have endpoints yet
          setRecords([]);
          setLoadingRecords(false);
          return;
      }
      
      const response = await api.get(`${API}${endpoint}`);
      const data = Array.isArray(response.data) ? response.data : response.data.orders || response.data.records || [];
      setRecords(data);
    } catch (error) {
      console.error('Failed to load records:', error);
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const generateFHIR = async () => {
    if (!recordId) {
      toast.error('Please select a record first');
      return;
    }

    setLoading(true);
    setFhirBundle(null);
    setValidation(null);

    try {
      const response = await api.post(`${API}/api/abdm/fhir/generate`, {
        document_type: documentType,
        record_id: recordId
      });

      setFhirBundle(response.data.bundle);
      setValidation(response.data.validation);
      setActiveView('visual');
      
      if (response.data.validation?.valid) {
        toast.success('FHIR bundle generated successfully!');
      } else {
        toast.warning('FHIR bundle generated with validation issues');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate FHIR bundle';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const validateBundle = async () => {
    if (!fhirBundle) return;

    try {
      const response = await api.post(`${API}/api/abdm/fhir/validate`, fhirBundle);
      setValidation(response.data);
      setActiveView('validation');
    } catch (error) {
      toast.error('Failed to validate bundle');
    }
  };

  const copyToClipboard = () => {
    if (!fhirBundle) return;
    navigator.clipboard.writeText(JSON.stringify(fhirBundle, null, 2));
    toast.success('FHIR bundle copied to clipboard');
  };

  const downloadBundle = () => {
    if (!fhirBundle) return;
    
    const blob = new Blob([JSON.stringify(fhirBundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fhir-${documentType}-${recordId.slice(0, 8)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('FHIR bundle downloaded');
  };

  const toggleResource = (resourceId) => {
    setExpandedResources(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  const selectRecord = (record) => {
    setRecordId(record.id);
    setSelectedRecord(record);
    setFhirBundle(null);
    setValidation(null);
  };

  const getRecordDisplayName = (record) => {
    if (documentType === 'prescription') {
      return record.prescription_number || record.id?.slice(0, 8);
    } else if (documentType === 'discharge_summary') {
      return `DS-${record.id?.slice(0, 8)}`;
    } else if (documentType === 'lab_report') {
      return record.order_number || record.test_name || record.id?.slice(0, 8);
    } else if (documentType === 'op_consultation') {
      return `MR-${record.id?.slice(0, 8)}`;
    } else if (documentType === 'invoice') {
      return record.invoice_number || record.id?.slice(0, 8);
    }
    return record.id?.slice(0, 8);
  };

  const filteredRecords = records.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const displayName = getRecordDisplayName(record).toLowerCase();
    const diagnosis = (record.diagnosis || record.test_name || '').toLowerCase();
    return displayName.includes(query) || diagnosis.includes(query);
  });

  // Visual FHIR Resource Renderer
  const ResourceCard = ({ entry, index }) => {
    const resource = entry.resource;
    const resourceType = resource?.resourceType;
    const resourceId = resource?.id || index;
    const isExpanded = expandedResources[resourceId];
    const IconComponent = RESOURCE_ICONS[resourceType] || FileJson;

    return (
      <div className="border rounded-lg mb-2 overflow-hidden">
        <div 
          className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
          onClick={() => toggleResource(resourceId)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <IconComponent className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium">{resourceType}</p>
              <p className="text-xs text-gray-500">ID: {resourceId?.slice(0, 12)}...</p>
            </div>
          </div>
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
        
        {isExpanded && (
          <div className="p-4 bg-white border-t">
            {renderResourceContent(resource)}
          </div>
        )}
      </div>
    );
  };

  const renderResourceContent = (resource) => {
    if (!resource) return null;

    const renderValue = (value, key) => {
      if (value === null || value === undefined) return <span className="text-gray-400">null</span>;
      if (typeof value === 'boolean') return <Badge variant={value ? 'default' : 'secondary'}>{value.toString()}</Badge>;
      if (typeof value === 'number') return <span className="font-mono text-blue-600">{value}</span>;
      if (typeof value === 'string') {
        if (value.startsWith('urn:uuid:') || value.includes('/')) {
          return <span className="font-mono text-purple-600 text-xs">{value}</span>;
        }
        return <span className="text-gray-700">{value}</span>;
      }
      if (Array.isArray(value)) {
        return (
          <div className="ml-4 mt-1 space-y-1">
            {value.map((item, i) => (
              <div key={i} className="border-l-2 border-gray-200 pl-3">
                {renderValue(item, i)}
              </div>
            ))}
          </div>
        );
      }
      if (typeof value === 'object') {
        return (
          <div className="ml-4 mt-1 space-y-1">
            {Object.entries(value).map(([k, v]) => (
              <div key={k} className="flex items-start gap-2">
                <span className="text-xs font-medium text-gray-500 min-w-[100px]">{k}:</span>
                {renderValue(v, k)}
              </div>
            ))}
          </div>
        );
      }
      return <span>{String(value)}</span>;
    };

    // Key fields to show prominently based on resource type
    const keyFields = {
      'Patient': ['name', 'gender', 'birthDate', 'identifier', 'telecom', 'address'],
      'Practitioner': ['name', 'identifier', 'qualification'],
      'Organization': ['name', 'identifier', 'type', 'address'],
      'Composition': ['status', 'type', 'title', 'date', 'section'],
      'MedicationRequest': ['status', 'medicationCodeableConcept', 'dosageInstruction'],
      'Condition': ['clinicalStatus', 'code', 'subject'],
      'Encounter': ['status', 'class', 'period', 'reasonCode'],
      'DiagnosticReport': ['status', 'code', 'effectiveDateTime', 'result'],
      'Observation': ['status', 'code', 'valueQuantity', 'valueString', 'interpretation'],
      'Immunization': ['status', 'vaccineCode', 'occurrenceDateTime', 'lotNumber'],
      'DocumentReference': ['status', 'type', 'description', 'content'],
      'CarePlan': ['status', 'intent', 'activity'],
      'Invoice': ['status', 'identifier', 'totalGross', 'lineItem'],
    };

    const resourceType = resource.resourceType;
    const fieldsToShow = keyFields[resourceType] || Object.keys(resource).filter(k => k !== 'resourceType' && k !== 'id' && k !== 'meta');

    return (
      <div className="space-y-2">
        {fieldsToShow.map(field => {
          if (resource[field] === undefined) return null;
          return (
            <div key={field} className="flex items-start gap-2">
              <span className="text-sm font-medium text-gray-600 min-w-[120px]">{field}:</span>
              <div className="flex-1">{renderValue(resource[field], field)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FHIR Document Viewer</h1>
          <p className="text-gray-500">Preview and validate FHIR R4 bundles before ABDM submission</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-700">
            <Shield className="w-3 h-3 mr-1" /> ABDM Compliant
          </Badge>
          <Badge variant="outline">8 HI Types</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Record Selection */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-purple-600" />
              Select Record
            </CardTitle>
            <CardDescription>Choose a document type and record to generate FHIR</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Document Type */}
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={(v) => { setDocumentType(v); setRecordId(''); setSelectedRecord(null); setFhirBundle(null); }}>
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${type.color}`} />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-2">
              <Label>Search Records</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by ID, diagnosis..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="record-search-input"
                />
              </div>
            </div>

            {/* Records List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Available Records</Label>
                <Button variant="ghost" size="sm" onClick={loadRecords} disabled={loadingRecords}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${loadingRecords ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <ScrollArea className="h-[300px] border rounded-lg p-2">
                {loadingRecords ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  </div>
                ) : filteredRecords.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <FileText className="w-8 h-8 mb-2" />
                    <p className="text-sm">No records found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredRecords.map((record) => {
                      const isSelected = recordId === record.id;
                      const DocIcon = DOCUMENT_TYPES.find(d => d.value === documentType)?.icon || FileText;
                      return (
                        <div
                          key={record.id}
                          onClick={() => selectRecord(record)}
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-purple-100 border-2 border-purple-500' 
                              : 'bg-gray-50 border border-gray-200 hover:border-purple-300'
                          }`}
                          data-testid={`record-item-${record.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <DocIcon className={`w-4 h-4 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{getRecordDisplayName(record)}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {record.diagnosis || record.test_name || record.patient_name || 'No description'}
                              </p>
                            </div>
                            {isSelected && <CheckCircle2 className="w-4 h-4 text-purple-600" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Generate Button */}
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={generateFHIR}
              disabled={!recordId || loading}
              data-testid="generate-fhir-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileJson className="w-4 h-4 mr-2" />
                  Generate FHIR Bundle
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - FHIR Viewer */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-600" />
                  FHIR Bundle Preview
                </CardTitle>
                <CardDescription>
                  {fhirBundle ? `Bundle ID: ${fhirBundle.id?.slice(0, 12)}...` : 'Select a record and generate FHIR'}
                </CardDescription>
              </div>
              
              {fhirBundle && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={copyToClipboard}>
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadBundle}>
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!fhirBundle ? (
              <div className="flex flex-col items-center justify-center h-[500px] text-gray-500 border-2 border-dashed rounded-lg">
                <FileJson className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">No FHIR Bundle Generated</p>
                <p className="text-sm">Select a document type and record, then click Generate</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Validation Status */}
                {validation && (
                  <div className={`p-4 rounded-lg ${validation.valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className="flex items-center gap-3">
                      {validation.valid ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-yellow-600" />
                      )}
                      <div>
                        <p className={`font-medium ${validation.valid ? 'text-green-700' : 'text-yellow-700'}`}>
                          {validation.valid ? 'Valid FHIR Bundle' : 'Validation Issues Found'}
                        </p>
                        <p className="text-sm text-gray-600">
                          DHIS Compliant: {validation.dhis_compliant ? 'Yes' : 'No'}
                          {validation.warnings?.length > 0 && ` | ${validation.warnings.length} warning(s)`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* View Tabs */}
                <Tabs value={activeView} onValueChange={setActiveView}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="visual">Visual View</TabsTrigger>
                    <TabsTrigger value="json">JSON View</TabsTrigger>
                    <TabsTrigger value="validation">Validation</TabsTrigger>
                  </TabsList>

                  {/* Visual View */}
                  <TabsContent value="visual">
                    <ScrollArea className="h-[400px] border rounded-lg p-4">
                      {/* Bundle Header */}
                      <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <FileJson className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold">Bundle</span>
                          <Badge variant="outline">{fhirBundle.type}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-500">ID:</span> <span className="font-mono">{fhirBundle.id?.slice(0, 20)}...</span></div>
                          <div><span className="text-gray-500">Timestamp:</span> {new Date(fhirBundle.timestamp).toLocaleString()}</div>
                          <div><span className="text-gray-500">Entries:</span> {fhirBundle.entry?.length || 0}</div>
                        </div>
                      </div>

                      {/* Resources */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-700 mb-2">Resources ({fhirBundle.entry?.length || 0})</h4>
                        {fhirBundle.entry?.map((entry, index) => (
                          <ResourceCard key={index} entry={entry} index={index} />
                        ))}
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* JSON View */}
                  <TabsContent value="json">
                    <ScrollArea className="h-[400px]">
                      <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-sm font-mono overflow-x-auto">
                        {JSON.stringify(fhirBundle, null, 2)}
                      </pre>
                    </ScrollArea>
                  </TabsContent>

                  {/* Validation View */}
                  <TabsContent value="validation">
                    <div className="p-4 border rounded-lg h-[400px] overflow-auto">
                      {validation ? (
                        <div className="space-y-4">
                          {/* Status */}
                          <div className="flex items-center gap-3">
                            {validation.valid ? (
                              <div className="flex items-center gap-2 text-green-600">
                                <CheckCircle2 className="w-6 h-6" />
                                <span className="font-semibold">Bundle is Valid</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-red-600">
                                <XCircle className="w-6 h-6" />
                                <span className="font-semibold">Validation Failed</span>
                              </div>
                            )}
                          </div>

                          {/* DHIS Compliance */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Shield className={`w-5 h-5 ${validation.dhis_compliant ? 'text-green-600' : 'text-yellow-600'}`} />
                              <span className="font-medium">DHIS Compliance:</span>
                              <Badge className={validation.dhis_compliant ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {validation.dhis_compliant ? 'Compliant' : 'Non-Compliant'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Digital Health Incentive Scheme eligibility
                            </p>
                          </div>

                          {/* Issues */}
                          {validation.issues?.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="font-medium text-red-600 flex items-center gap-2">
                                <XCircle className="w-4 h-4" /> Issues ({validation.issues.length})
                              </h4>
                              {validation.issues.map((issue, i) => (
                                <div key={i} className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                  {issue}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Warnings */}
                          {validation.warnings?.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="font-medium text-yellow-600 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" /> Warnings ({validation.warnings.length})
                              </h4>
                              {validation.warnings.map((warning, i) => (
                                <div key={i} className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                                  {warning}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* All Good */}
                          {validation.valid && validation.issues?.length === 0 && validation.warnings?.length === 0 && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                              <p className="font-medium text-green-700">All Checks Passed</p>
                              <p className="text-sm text-green-600">Bundle is ready for ABDM submission</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                          <Info className="w-8 h-8 mb-2" />
                          <p>Generate a FHIR bundle to see validation results</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Supported ABDM Health Information Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {DOCUMENT_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <div key={type.value} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Icon className={`w-5 h-5 ${type.color}`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.collection}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
